import uiConfig from './uiConfig.json';

/**
 * Single source of truth for the JSON-driven UI.
 *
 * All UI configuration lives in `uiConfig.json`. This module re-exports each
 * section as a named constant and documents the fields (JSON cannot hold
 * comments). Components named in config are resolved via `componentRegistry`.
 *
 * Sections:
 * - groups            Per-group capability flags + default group.
 * - dashboardSections Sidebar accordions.
 * - dashboardRoutes   Dashboard route -> component map.
 * - pageLayout        Panel composition per page region.
 * - navigation        Header tabs + app-launcher menu items.
 */

/**
 * groups.byName[name]:
 * - latestOnly:   Group has no release versions; uses latest-only schema fetch.
 * - fullFeatured: Group exposes Events, Release History, Workflow, Manage Release.
 * groups.defaultGroup: Group selected on first load.
 */
export const GROUPS_CONFIG = uiConfig.groups.byName;
export const DEFAULT_GROUP = uiConfig.groups.defaultGroup;

/**
 * Resolve the backend URL path segment for a group's guide resource
 * (`rt_config.url_path_segment`, e.g. 'fhir_ig'). Iframe-domain groups use it
 * to build FHIR IG endpoint paths. Falls back to 'fhir_ig'.
 */
export function getGroupUrlPathSegment(groupName) {
  return GROUPS_CONFIG[groupName]?.urlPathSegment || 'fhir_ig';
}

/**
 * dashboardSections (sidebar accordions), per domain/group:
 * - default:      Section list used when a group has no specific override.
 * - byGroup[name]:Section list for a specific group (replaces default entirely).
 *
 * Each section entry:
 * - id:                 Stable key for open/close state and React keys.
 * - title:              Display label on the accordion header. Safe to rename.
 * - type:               Entity type passed downstream (navigation + schema fetch).
 * - itemsSource:        Derived list key ('events' | 'resources' | 'complexDataTypes').
 * - enableHighlight:    Highlight search matches in item names.
 * - needsSchemaSetters: Pass schema setter props (inline validation of parentless items).
 * - grow:               Flex the section to fill remaining height.
 *
 * Use `getDashboardSections(groupName)` to resolve the right list for a group.
 */
export const DASHBOARD_SECTIONS = uiConfig.dashboardSections;

/**
 * Resolve the accordion sections for a group, falling back to `default`
 * when the group has no specific override.
 */
export function getDashboardSections(groupName) {
  return DASHBOARD_SECTIONS.byGroup?.[groupName] || DASHBOARD_SECTIONS.default || [];
}

/**
 * dashboardRoutes[]:
 * - path:      React Router path (relative to /dashboard).
 * - component: Registry key, or null to render no element (falls back to MiddleContent).
 */
export const DASHBOARD_ROUTES = uiConfig.dashboardRoutes;

/**
 * pageLayout[page].{header,body}[]:
 * - component:        Registry key for the panel.
 * - props:            Prop names the panel receives from the page's prop bag.
 * - wrapperClassName: Optional wrapper div class.
 */
export const PAGE_LAYOUT = uiConfig.pageLayout;

/**
 * Resolve the dashboard panel layout for a group. Groups with a specific
 * layout (e.g. Clinical, iframe domain) override the default `dashboard`
 * layout entirely; all others fall back to it.
 */
export function getDashboardLayout(groupName) {
  return PAGE_LAYOUT.byGroup?.[groupName] || PAGE_LAYOUT.dashboard;
}

/**
 * navigation.tabs[] / navigation.menu[]:
 * - itemName / value:     Label / tab value.
 * - path:                 Static navigation target.
 * - pathRef:              Dynamic path resolved at runtime ('lastDashboardPath').
 * - envKey:               process.env var holding an external URL.
 * - external:             Open in a new tab instead of router navigation.
 * - requiresFullFeatured: Only show for full-featured groups.
 * - requiresManageReleaseRole: Only show to users in the admin/modelling
 *                         Cognito groups (see src/config/roles.js). Hiding the
 *                         entry is cosmetic — the page enforces this too.
 * - disabled:             Render a disabled tab placeholder.
 * - labelDefault:         Tab label used when the group has no override.
 * - labelByGroup:         Per-data-group tab label overrides, keyed by group
 *                         name (Lab / DiabetesCare / Clinical / MolLab).
 * - labelByGroupSingular: Per-group label used instead of labelByGroup when the
 *                         group's countSourceByGroup list holds exactly one
 *                         item (e.g. a single FHIR Implementation Guide).
 * - countSourceByGroup:   groupData key whose length decides singular/plural.
 */
export const NAVIGATION = uiConfig.navigation;

export default uiConfig;
